import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { TextChunkSchema } from './text-chunks';
import { commaDelimitedList } from './utils';

/**
 * Represents a node in the knowledge graph
 */
export const KnowledgeGraphNodeSchema = z.object({
  knowledgeGraphNodeId: z.string().nanoid(),
  entityName: z.string(),
  entityType: z.string(),
  extractionModel: z.string(),
  createdAt: z.coerce.date(),
});

export type KnowledgeGraphNode = z.infer<typeof KnowledgeGraphNodeSchema>;

/**
 * Represents a relationship between two nodes in the knowledge graph
 */
export const KnowledgeGraphEdgeSchema = z.object({
  knowledgeGraphEdgeId: z.string().nanoid(),
  sourceKnowledgeGraphNodeId: z.string(),
  targetKnowledgeGraphNodeId: z.string(),
  relationshipType: z.string(),
  createdAt: z.coerce.date(),
});

export type KnowledgeGraphEdge = z.infer<typeof KnowledgeGraphEdgeSchema>;

/**
 * Represents the association between a knowledge graph node and a text chunk
 */
export const KnowledgeGraphNodeTextChunkSchema = z.object({
  knowledgeGraphNodeTextChunkId: z.string().nanoid(),
  knowledgeGraphNodeId: z.string(),
  textChunkId: z.string(),
  createdAt: z.coerce.date(),
});

export type KnowledgeGraphNodeTextChunk = z.infer<
  typeof KnowledgeGraphNodeTextChunkSchema
>;

// Query schemas for listing nodes
export const KnowledgeGraphNodesQuerySchema =
  createOffsetPaginationRequestQuerySchema(
    z.enum(['createdAt', 'entityName', 'entityType']),
  ).merge(
    z.object({
      knowledgeGraphNodeIds: commaDelimitedList(
        KnowledgeGraphNodeSchema.shape.knowledgeGraphNodeId.array(),
      ).optional(),
      entityTypes: commaDelimitedList(z.string().array()).optional(),
      textChunkIds: commaDelimitedList(
        TextChunkSchema.shape.textChunkId.array(),
      ).optional(),
      sourceIds: commaDelimitedList(z.string().array()).optional(),
    }),
  );

export type KnowledgeGraphNodesQuery = z.infer<
  typeof KnowledgeGraphNodesQuerySchema
>;

// Query schemas for listing edges
export const KnowledgeGraphEdgesQuerySchema =
  createOffsetPaginationRequestQuerySchema(
    z.enum(['createdAt', 'relationshipType']),
  ).merge(
    z.object({
      knowledgeGraphEdgeIds: commaDelimitedList(
        KnowledgeGraphEdgeSchema.shape.knowledgeGraphEdgeId.array(),
      ).optional(),
      sourceKnowledgeGraphNodeIds: commaDelimitedList(
        z.string().array(),
      ).optional(),
      targetKnowledgeGraphNodeIds: commaDelimitedList(
        z.string().array(),
      ).optional(),
      relationshipTypes: commaDelimitedList(z.string().array()).optional(),
      sourceIds: commaDelimitedList(z.string().array()).optional(),
    }),
  );

export type KnowledgeGraphEdgesQuery = z.infer<
  typeof KnowledgeGraphEdgesQuerySchema
>;

// Query schemas for listing node text chunks
export const KnowledgeGraphNodeTextChunksQuerySchema =
  createOffsetPaginationRequestQuerySchema(z.enum(['createdAt'])).merge(
    z.object({
      knowledgeGraphNodeIds: commaDelimitedList(
        KnowledgeGraphNodeSchema.shape.knowledgeGraphNodeId.array(),
      ).optional(),
      textChunkIds: commaDelimitedList(
        TextChunkSchema.shape.textChunkId.array(),
      ).optional(),
    }),
  );

export type KnowledgeGraphNodeTextChunksQuery = z.infer<
  typeof KnowledgeGraphNodeTextChunksQuerySchema
>;

// Arrays of entities
export const KnowledgeGraphNodesSchema = z.array(KnowledgeGraphNodeSchema);
export const KnowledgeGraphEdgesSchema = z.array(KnowledgeGraphEdgeSchema);
export const KnowledgeGraphNodeTextChunksSchema = z.array(
  KnowledgeGraphNodeTextChunkSchema,
);

export type KnowledgeGraphNodes = z.infer<typeof KnowledgeGraphNodesSchema>;
export type KnowledgeGraphEdges = z.infer<typeof KnowledgeGraphEdgesSchema>;
export type KnowledgeGraphNodeTextChunks = z.infer<
  typeof KnowledgeGraphNodeTextChunksSchema
>;
